import { default as posthog } from 'posthog-js';
import { CASINO_GAME_TAGS } from '../data-types/casino/casinoConstants';
import { CASINO_CATEGORY_IDS, CASINO_TAG_ID, GAME_REACHED_FROM } from '../data-types/casino/casinoEnums';
import { FilterInfo } from '../data-types/casino/casinoInterfaces';
import { APP_AREA } from '../data-types/enums';
import { POST_HOG_EVENT, POSTHOG_FEATURE_FLAG_KEY, POSTHOG_FEATURE_FLAG_VALUE } from '../data-types/posthog/posthogEnums';
import { PostHogIdentifiers, PostHogTrackStreamData } from '../data-types/posthog/posthogInterfaces';
import { SEARCH_PARAM_KEY } from '../data-types/url/urlEnums';
import { POST_HOG_ENABLED } from '../initialisation/initialisation';
import { setBetPlacementRocketAnimation, setNetPromoterScoreFlag, setReorderCasinoFilters } from '../state/actions/customizationActions';
import { getCasinoCategoryNameById } from './casino';
import { getSearchParamValue } from './search';
import { getSportNameByID } from './sports';

export const tryToInitiatePostHog = () => {
  if (POST_HOG_ENABLED) {
    const POST_HOG_KEY = process.env.REACT_APP_POST_HOG_KEY || 'INEXISTENT';
    const POST_HOG_HOST = process.env.REACT_APP_POST_HOG_HOST || 'INEXISTENT';

    posthog.init(POST_HOG_KEY, { api_host: POST_HOG_HOST, autocapture: false, capture_pageview: false, disable_session_recording: true });
  }
};

export const resetPostHog = (force = false) => {
  if (POST_HOG_ENABLED) {
    posthog.reset(force);
  }
};

export const postHogIdentifyUser = (userId: number, identifiers: PostHogIdentifiers) => {
  if (POST_HOG_ENABLED) {
    posthog.identify(`${userId}`, { ...identifiers });
  }
};

export const postHogCapture = (eventName: POST_HOG_EVENT, identifiers: PostHogIdentifiers) => {
  if (POST_HOG_ENABLED) {
    posthog.capture(eventName, { ...identifiers });
  }
};

export const postHogStartSessionRecording = () => {
  if (POST_HOG_ENABLED && !posthog.sessionRecordingStarted()) {
    posthog.startSessionRecording();
  }
};

const isPostHogFeatureFlagEnabled = async (key: POSTHOG_FEATURE_FLAG_KEY, enabledValue: POSTHOG_FEATURE_FLAG_VALUE | boolean) => {
  if (POST_HOG_ENABLED) {
    await posthog.onFeatureFlags(async () => {
      const actualValue = await posthog.getFeatureFlag(key, { send_event: false });
      handlePostHogFeaturedFlagValue(key, enabledValue, actualValue);
    });
  }
};

const handlePostHogFeaturedFlagValue = (
  key: POSTHOG_FEATURE_FLAG_KEY,
  enabledValue: POSTHOG_FEATURE_FLAG_VALUE | boolean,
  actualValue?: string | boolean,
) => {
  switch (key) {
    case POSTHOG_FEATURE_FLAG_KEY.NET_PROMOTER_SCORE:
      setNetPromoterScoreFlag(enabledValue === actualValue);
      break;
    case POSTHOG_FEATURE_FLAG_KEY.CASINO_FILTER_REORDER:
      setReorderCasinoFilters(enabledValue === actualValue);
      break;
    case POSTHOG_FEATURE_FLAG_KEY.BET_PLACEMENT_PLACE_ANIMATION:
      setBetPlacementRocketAnimation(enabledValue === actualValue);
      break;
    default:
      break;
  }
};

const checkReorderCasinoFilters = async () => {
  try {
    await isPostHogFeatureFlagEnabled(POSTHOG_FEATURE_FLAG_KEY.CASINO_FILTER_REORDER, POSTHOG_FEATURE_FLAG_VALUE.TEST);
  } catch (e) {
    setReorderCasinoFilters(false);
  }
};

const checkNetPromoterScore = async () => {
  try {
    await isPostHogFeatureFlagEnabled(POSTHOG_FEATURE_FLAG_KEY.NET_PROMOTER_SCORE, true);
  } catch (e) {
    setNetPromoterScoreFlag(false);
  }
};

const checkBetPlacementAnimation = async () => {
  try {
    await isPostHogFeatureFlagEnabled(POSTHOG_FEATURE_FLAG_KEY.BET_PLACEMENT_PLACE_ANIMATION, POSTHOG_FEATURE_FLAG_VALUE.TEST);
  } catch (e) {
    setNetPromoterScoreFlag(false);
  }
};

export const checkPostHogFeaturedFlags = () => {
  checkNetPromoterScore();
  checkReorderCasinoFilters();
  checkBetPlacementAnimation();
};

export const revertPostHogFeaturedFlags = () => {
  setNetPromoterScoreFlag(false);
};

const getCasinoGamePostHogEventName = (casinoCategory?: number) => {
  switch (casinoCategory) {
    case CASINO_CATEGORY_IDS.FEATURED:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_FEATURED;
    case CASINO_CATEGORY_IDS.SLOTS:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_SLOTS;
    case CASINO_CATEGORY_IDS.LIVE_CASINO:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_LIVE_CASINO;
    case CASINO_CATEGORY_IDS.TABLE_GAMES:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_TABLE_GAMES;
    case CASINO_CATEGORY_IDS.VIRTUAL_SPORTS:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_VIRTUAL_SPORTS;
    default:
      return POST_HOG_EVENT.OPEN_BESPOKE_CASINO_GAME_UNTRACKED_CATEGORY;
  }
};

export const posthogTrackOpenCasinoGamefromSearch = (
  gameId: number,
  indexInList: number | undefined,
  viewedFrom: GAME_REACHED_FROM | undefined,
  gameTags: string[],
) => {
  const searchTerm = getSearchParamValue(SEARCH_PARAM_KEY.SEARCH_TERM);

  if (searchTerm && viewedFrom === GAME_REACHED_FROM.SEARCH && typeof indexInList === 'number') {
    postHogCapture(POST_HOG_EVENT.CASINO_GAME_OPENED_FROM_SEARCH, { gameId, searchTerm, gameTags, position: indexInList + 1 });
  }
};

export const posthogTrackOpenCasinoGamefromFilter = (
  gameId: number,
  indexInList: number | undefined,
  filterInfo: FilterInfo | undefined,
  casinoCategory: number | undefined,
  gameTags: string[],
) => {
  if (filterInfo && typeof indexInList === 'number' && typeof casinoCategory === 'number') {
    const page = getCasinoCategoryNameById(casinoCategory);

    postHogCapture(POST_HOG_EVENT.CASINO_GAME_OPENED_FROM_FILTER, {
      gameId,
      page,
      gameTags,
      filterId: filterInfo.id,
      filterName: filterInfo.name,
      filterPosition: filterInfo.index + 1,
      gamePositionInFilter: indexInList + 1,
    });
  }
};

export const posthogTrackOpenCasinoGamefromRecommended = (
  gameId: number,
  indexInList: number | undefined,
  isBespokeGame: boolean | undefined,
  casinoCategory: number | undefined,
  gameTags: string[],
) => {
  if (isBespokeGame && typeof indexInList === 'number' && typeof casinoCategory === 'number') {
    const eventName = getCasinoGamePostHogEventName(casinoCategory);

    postHogCapture(eventName, { gameId, gameTags, position: indexInList + 1 });
  }
};

export const posthogTrackOpenCasinoGamefromInstantGames = (
  gameId: number,
  indexInList: number | undefined,
  viewedFrom: GAME_REACHED_FROM | undefined,
) => {
  if (viewedFrom === GAME_REACHED_FROM.INSTANT_GAMES && typeof indexInList === 'number') {
    postHogCapture(POST_HOG_EVENT.CASINO_GAME_OPENED_FROM_INSTANT_GAMES, { gameId, position: indexInList + 1 });
  }
};

export const posthogTrackOpenCasinoGamefromSportsbookFeatured = (
  gameId: number,
  indexInList: number | undefined,
  viewedFrom: GAME_REACHED_FROM | undefined,
  gameTags: string[],
) => {
  if (viewedFrom === GAME_REACHED_FROM.SPORTSBOOK_FEATURED && typeof indexInList === 'number') {
    postHogCapture(POST_HOG_EVENT.CASINO_GAME_OPENED_FROM_SPORTSBOOK_FEATURED, { gameId, gameTags, position: indexInList + 1 });
  }
};

export const posthogTrackCasinoGameOpen = (
  gameId: number,
  indexInList: number | undefined,
  viewedFrom: GAME_REACHED_FROM | undefined,
  isBespokeGame: boolean | undefined,
  casinoCategory: number | undefined,
  filterInfo: FilterInfo | undefined,
  gameTagIds: CASINO_TAG_ID[] | null | undefined,
) => {
  const gameTagNames = gameTagIds
    ? gameTagIds
        .map(tagId => {
          const tag = CASINO_GAME_TAGS.find(item => item.id === tagId);
          return (tag && tag.name) || '';
        })
        .filter(item => item !== '')
    : [];

  posthogTrackOpenCasinoGamefromSearch(gameId, indexInList, viewedFrom, gameTagNames);
  posthogTrackOpenCasinoGamefromFilter(gameId, indexInList, filterInfo, casinoCategory, gameTagNames);
  posthogTrackOpenCasinoGamefromRecommended(gameId, indexInList, isBespokeGame, casinoCategory, gameTagNames);
  posthogTrackOpenCasinoGamefromInstantGames(gameId, indexInList, viewedFrom);
  posthogTrackOpenCasinoGamefromSportsbookFeatured(gameId, indexInList, viewedFrom, gameTagNames);
};

export const getPostHogSurveyResponseKeyEnding = (questionIndex: number) => (questionIndex > 0 ? `_${questionIndex}` : '');

export const posthogTrackStreamOpen = (data: PostHogTrackStreamData) => {
  const { appArea, supplier, sportId, openedFrom } = data;
  const sportName = getSportNameByID(sportId);

  switch (appArea) {
    case APP_AREA.FEATURED:
      postHogCapture(POST_HOG_EVENT.STREAM_FEATURED, { supplier, sport: sportName });
      break;
    case APP_AREA.MY_BETS:
      postHogCapture(POST_HOG_EVENT.STREAM_MY_BETS, { supplier, sport: sportName });
      break;
    case APP_AREA.EVENT_DETAILS:
    case APP_AREA.SINGLE_SPORT:
      let identifiers: object = { supplier, sport: sportName };

      if (openedFrom) {
        identifiers = {
          ...identifiers,
          openedFrom,
        };
      }

      postHogCapture(POST_HOG_EVENT.STREAM_SPORT, { ...identifiers });
      break;
    default:
      break;
  }
};
